import dayjs from 'dayjs';
import arLocal from 'dayjs/locale/ar';
import deLocal from 'dayjs/locale/de';
import enLocal from 'dayjs/locale/en';
import frLocal from 'dayjs/locale/fr';
import itLocal from 'dayjs/locale/it';
import ptLocal from 'dayjs/locale/pt';
import duration from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  ConfigType,
  Date,
  LanguageCode,
  LanguageItems,
  ManipulateType,
} from '@type';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isToday);

export const timestampToDate = (timestamp: number): Date => {
  return dayjs.unix(timestamp);
};

export const convertToDate = (date: ConfigType): Date => {
  return dayjs(date);
};

export const getDateNow = () => {
  return dayjs();
};

export const getTimestamp = () => {
  return dayjs().valueOf();
};

export const updateDateLocale = (lang: LanguageCode) => {
  switch (lang) {
    case LanguageItems.German.code:
      dayjs.locale(deLocal);
      break;
    case LanguageItems.Portuguese.code:
      dayjs.locale(ptLocal);
      break;
    case LanguageItems.French.code:
      dayjs.locale(frLocal);
      break;
    case LanguageItems.Arabic.code:
      dayjs.locale(arLocal);
      break;
    case LanguageItems.Italian.code:
      dayjs.locale(itLocal);
      break;
    default:
      dayjs.locale(enLocal);
      break;
  }
};

export const dateStartOf = (date: Date, unit: dayjs.OpUnitType): Date => {
  return date.startOf(unit);
};

export const dateAdd = (
  date: Date,
  value: number,
  unit: ManipulateType
): Date => {
  return date.add(value, unit);
};

export const getDateUTCOffset = (): number => {
  return getDateNow().utcOffset();
};

export const dateIsBefore = (dateA: Date, dateB: Date): boolean => {
  return dateA.isBefore(dateB);
};

export const dateIsBeforeNow = (date: Date): boolean => {
  return dateIsBefore(date, getDateNow());
};

export const dateIsAfter = (dateA: Date, dateB: Date): boolean => {
  return dateA.isAfter(dateB);
};

export const dateIsAfterNow = (date: Date): boolean => {
  return dateIsAfter(date, getDateNow());
};

export const getDateDiff = (dateA: Date, dateB: Date): number => {
  return dateA.diff(dateB);
};

export const getDateDiffFromNow = (dateA: Date): number => {
  return dateA.diff(getDateNow());
};

export const getDifferenceInDays = (dateA: Date, dateB: Date): number => {
  return dateA.diff(dateB, 'day');
};

export const getDateDuration = (timestamp: number): duration.Duration => {
  return dayjs.duration(timestamp);
};

export const getDateDurationSeconds = (duration: duration.Duration): number => {
  return duration.seconds();
};

export const getDateDurationMinutes = (duration: duration.Duration): number => {
  return duration.minutes();
};

export const getDateDurationHours = (duration: duration.Duration): number => {
  return duration.hours();
};

export const getDateDurationDays = (duration: duration.Duration): number => {
  return duration.days();
};

export const formatDuration = (
  duration: duration.Duration,
  formatStr?: string
) => {
  return duration.format(formatStr);
};

export const getDateHour = (date: Date): number => {
  return date.hour();
};

export const isDateInstance = (date: unknown): boolean => {
  return dayjs.isDayjs(date);
};

export const formatDate = (date: ConfigType, formatString?: string) => {
  return convertToDate(date).format(formatString);
};

export const convertToPrimitiveDate = (date: Date) => date.toDate();

export const isDateToday = (date: Date) => date.isToday();

export const isSameDate = (
  date1: Date,
  date2: Date,
  granularity: ManipulateType = 'days'
) => date1.isSame(date2, granularity);

export const subtractPeriodFromNowDate = (
  value: number,
  unit: ManipulateType
): Date => {
  return dayjs().subtract(value, unit);
};

export const dateToUnix = (date: ConfigType): number => {
  return dayjs(date).unix();
};

export const dateToUnixMilliSecond = (date: ConfigType): number => {
  return dayjs(date).unix() * 1000;
};

export const unixEpochsToTimezoneDate = (unixEpochs: number) => {
  return dateStartOf(
    timestampToDate(unixEpochs - 60 * getDateUTCOffset()),
    'minute'
  );
};

export const getUTCDate = () => {
  return dayjs().utc().format();
};
